<template>
  <div class="flex flex-col bg-[--v3-input-lists-inputlistheaderbg]">
    <div class="select-none bg-[--v3-input-lists-inputlistheaderbg] px-[12px] py-[8px]">
      <h2 class="v3-body-semibold-600-line-height-1.5 ml-[22px] flex items-center justify-between text-[--v3-global-textsecondarycolor]">
        <span>MY KEYWORD LISTS</span>
        <span>{{ quotaNoneDaily.keyword_list_builder.current_usage > quotaNoneDaily.keyword_list_builder.feature_quota ? quotaNoneDaily.keyword_list_builder.feature_quota : quotaNoneDaily.keyword_list_builder.current_usage }}/{{ quotaNoneDaily.keyword_list_builder.feature_quota }} lists</span>
      </h2>
    </div>
    <div class="flex h-[294px] flex-1 flex-col bg-[--v3-root-surface-card]">
      <div v-if="loading" class="flex-1 px-[12px] py-[8px]">
        <Skeleton height="10px" class="mb-[8px]" />
        <Skeleton height="10px" class="mb-[8px]" />
        <Skeleton height="10px" class="mb-[8px]" />
      </div>
      <PerfectScrollbar
        v-else
        ref="scrollbarRef"
        class="flex-1"
        @dragover.prevent="handleDragOverList"
      >
        <KeywordModalListItem
          v-for="item in lists"
          :key="item.id"
          :list-id="item.id"
          :list-name="item.name"
          :color="item.color"
          :keywords="item.keywords"
          :loading="listsLoading.has(item.id)"
          :dragging-keyword="props.draggingKeyword"
          :max="QUOTA.keyword_lists"
          :selected="selectedListId === item.id"
          @click="$emit('toggle', item.id, item.keywords)"
          @on-add-keyword-to-list="handleAddKeywordToList"
        />
      </PerfectScrollbar>
      <div class="px-[12px] py-[4]">
        <Divider class="m-0" />
      </div>
      <IconLabel
        v-tooltip="quotaNoneDaily.keyword_list_builder.quota_exceeded ? 'Maximum keyword lists has been reached.' : null"
        label="New Keyword List"
        icon="plus"
        :disabled="quotaNoneDaily.keyword_list_builder.quota_exceeded"
        class="px-[12px] py-[9px]"
        @click="quotaNoneDaily.keyword_list_builder.quota_exceeded ? null : $emit('create')"
      />
    </div>
    <div class="flex bg-[--v3-blue-gray-bluegray-50]">
      <RouterLink :to="{ name: 'keyword-lists' }" class="w-full px-[12px] py-[15px]">
        <IconLabel
          icon="list"
          label="Manage Lists"
          align="center"
        />
      </RouterLink>
      <div class="h-full w-px py-[10px]">
        <Divider layout="vertical" class="m-0" />
      </div>
      <a href="/competitor-sales" class="w-full px-[12px] py-[15px]">
        <IconLabel
          icon="shop-icon"
          label="Tracked Shops"
          align="center"
        />
      </a>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import Divider from 'primevue/divider';
import Skeleton from 'primevue/skeleton';
import { ref } from 'vue';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

import IconLabel from '@/common/components/atoms/IconLabel';
import KeywordModalListItem from '@/common/components/atoms/KeywordModalListItem';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { getAppContext } from '@/helpers/get-app-context';

interface Props {
  draggingKeyword?: string | undefined | null
  lists?: {
    color: string
    id: number
    keywords: {
      id: number
      keyword: string
    }[]
    name: string
  }[]
  listsLoading: Set<number>
  loading?: boolean
  selectedListId?: number | undefined | null
}

const props = withDefaults(defineProps<Props>(), {
  boolean: false,
  draggingKeyword: undefined,
  lists: null,
  selectedListId: undefined,
});

const emit = defineEmits<{
  toggle: [listId: number, keywords: { id: number, keyword: string }[]],
  create: [],
  onAddKeywordToList: [listId: number, keyword: string],
}>();

const { QUOTA } = getAppContext();
const { quotaNoneDaily } = storeToRefs(useSubscriptionStore());

const AUTO_SCROLL_EDGE_THRESHOLD = 40;
const AUTO_SCROLL_STEP = 14;
const scrollbarRef = ref<{ $el: HTMLElement } | null>(null);

const getScrollContainer = (): HTMLElement | null => {
  const root = scrollbarRef.value?.$el;
  if (!root) {
    return null;
  }

  return root.classList.contains('ps')
    ? root
    : root.querySelector('.ps') ?? root;
};

const handleAddKeywordToList = (listId: number) => {
  emit('onAddKeywordToList', listId, window.DRAGGING_KEYWORD);
};

const handleDragOverList = (event: DragEvent) => {
  if (!props.draggingKeyword) {
    return;
  }

  const scrollContainer = getScrollContainer();
  if (!scrollContainer) {
    return;
  }

  const rect = scrollContainer.getBoundingClientRect();
  const cursorY = event.clientY;

  if (cursorY <= rect.top + AUTO_SCROLL_EDGE_THRESHOLD) {
    scrollContainer.scrollTop -= AUTO_SCROLL_STEP;
    return;
  }

  if (cursorY >= rect.bottom - AUTO_SCROLL_EDGE_THRESHOLD) {
    scrollContainer.scrollTop += AUTO_SCROLL_STEP;
  }
};
</script>
